import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Crown, Download, ArrowLeft, Award, Sparkles, Gift, Lock, AlertCircle } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import MyWinsPicker from '../components/ambassador/MyWinsPicker';
import { useAuth } from '../contexts/AuthContext';

const API = `${window.location.origin}/api`;
const LOGO_URL = '/api/files/jsfragrance/products/be3c5fd6-5c5e-4e28-9eca-fed1e5e06eea.png';

/**
 * Printable certificate page for Top Seller of the Month.
 *
 * Design intent:
 *  - Behaves like a luxury award certificate. Print = 1 single landscape A4
 *    page, NEVER split across pages. Achieved by:
 *      • Fixed inner dimensions (10.5in × 7.5in) inside an outer @page wrapper
 *      • `page-break-inside: avoid` on the certificate frame
 *      • Scaling the certificate to fit when viewed on smaller screens
 *  - Ornate triple-border, gold-foil gradient frame, watermark seal,
 *    handwritten-style recipient name, serial number for legitimacy,
 *    gold ribbon medallion, and an embossed-feeling signature block.
 *  - The user can also CLAIM their prize from this page (see button).
 */
export default function CertificatePage() {
  const { year, month } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [claim, setClaim] = useState(null); // existing claim record, if any
  const [myAmbId, setMyAmbId] = useState(null); // logged-in user's ambassador id (null = not an ambassador)

  const y = parseInt(year, 10) || new Date().getFullYear();
  const m = parseInt(month, 10) || new Date().getMonth() + 1;

  // The prize can only be claimed AFTER the month closes — the leaderboard
  // can still flip until the last day. We treat (y, m) as "open" if it's
  // the current calendar month or in the future. The backend enforces the
  // same rule; this client-side check just keeps the button from showing
  // when the API would reject the call anyway.
  const monthIsOver = (() => {
    const now = new Date();
    if (y < now.getFullYear()) return true;
    if (y > now.getFullYear()) return false;
    return m < now.getMonth() + 1;
  })();

  // Claim window — 7 days starting on the first day AFTER the win month.
  // Past this window, the prize expires and we hide the Claim button on
  // past-month certificates. Negative window for the current month is
  // ignored because `monthIsOver === false` already handles that case.
  const CLAIM_WINDOW_DAYS = 7;
  const claimDeadline = new Date(Date.UTC(y, m, 1 + CLAIM_WINDOW_DAYS)); // m is 1-indexed; Date treats month 0-indexed so this lands on day (CLAIM_WINDOW_DAYS) of the NEXT month
  const claimDaysLeft = Math.max(0, Math.ceil((claimDeadline.getTime() - Date.now()) / 86400000));
  const claimExpired = monthIsOver && Date.now() > claimDeadline.getTime();

  useEffect(() => {
    axios.get(`${API}/leaderboard/top-seller?year=${y}&month=${m}`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [y, m]);

  // Resolve the signed-in user's ambassador id so we can compare against
  // the top seller. WITHOUT this check, the page used to address every
  // viewer as "you're #1" — leaking the winner's status to non-winners.
  useEffect(() => {
    if (!user || !user.email) { setMyAmbId(null); return; }
    const token = localStorage.getItem('auth_token');
    if (!token) { setMyAmbId(null); return; }
    axios.get(`${API}/ambassador/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setMyAmbId(r.data?.ambassador?.id || null))
      .catch(() => setMyAmbId(null));
  }, [user]);

  // True iff the signed-in viewer is the actual top seller for (y, m).
  // All "you" language and the Claim Prize button gate on this.
  const isMe = !!(myAmbId && data?.top?.ambassador_id && myAmbId === data.top.ambassador_id);

  // Check if the signed-in ambassador has already claimed this month's prize
  useEffect(() => {
    if (!data?.top) return;
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    axios.get(`${API}/ambassador/me/prize-claims?year=${y}&month=${m}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => setClaim(r.data?.claim || null))
      .catch(() => {});
  }, [data, y, m]);

  // Preview-mode anti-grab keyboard shortcuts. Cancels:
  //   • Ctrl/Cmd + P (print)
  //   • Ctrl/Cmd + S (save page)
  //   • PrintScreen key (Windows hint — we can't actually block it but we
  //     can clear the clipboard so a paste won't land what they expected)
  //   • Right-click context menu inside the certificate area
  // Note: a determined user can defeat any of these. The goal is to make
  // it inconvenient enough that the user prefers to wait one extra week
  // for the legit version. Combined with the giant PREVIEW watermark,
  // this is plenty of deterrent for the ambassador audience.
  useEffect(() => {
    if (monthIsOver) return undefined;

    const onKey = (e) => {
      const k = (e.key || '').toLowerCase();
      const cmd = e.ctrlKey || e.metaKey;
      if (cmd && (k === 'p' || k === 's')) {
        e.preventDefault();
        toast.error("Download/print is locked until the month ends. Stay on top — your real certificate is just days away. 🔥", { duration: 4500 });
      }
      if (k === 'printscreen' && navigator.clipboard) {
        // PrintScreen actually captures BEFORE the page sees it on most
        // OSes, so we can't preempt it. We clobber the clipboard right
        // after as a small deterrent for casual users.
        setTimeout(() => {
          navigator.clipboard.writeText('Certificate preview — official version unlocks at month-end. JS Fragrance').catch(() => {});
        }, 50);
      }
    };
    const onContext = (e) => {
      // Block right-click only inside the certificate frame
      if (e.target.closest?.('.certificate-sheet')) {
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', onKey);
    window.addEventListener('contextmenu', onContext);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('contextmenu', onContext);
    };
  }, [monthIsOver]);

  // Deterministic serial number — same cert/month always renders the same code
  const serial = useMemo(() => {
    if (!data?.top) return '';
    const seed = `${y}-${String(m).padStart(2, '0')}-${data.top.ambassador_id || data.top.name || 'x'}`;
    let hash = 0;
    for (let i = 0; i < seed.length; i++) hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0;
    const positive = Math.abs(hash).toString(36).toUpperCase().padStart(6, '0').slice(-6);
    return `JSF-${y}${String(m).padStart(2, '0')}-${positive}`;
  }, [data, y, m]);

  const handleClaim = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      toast.error('Please sign in to claim your prize.');
      return;
    }
    const note = window.prompt(
      "🎁 Claim your Top Seller prize!\n\n" +
      "We'll send: cash bonus via GCash + 1 free 50ml bottle of your choice + spotlight feature.\n\n" +
      "Add a quick note (which scent you want, your GCash number, or any special request):",
      ''
    );
    if (note === null) return;
    setClaiming(true);
    try {
      const r = await axios.post(`${API}/ambassador/me/claim-prize`, {
        year: y, month: m, note: (note || '').slice(0, 500),
      }, { headers: { Authorization: `Bearer ${token}` } });
      setClaim(r.data?.claim);
      toast.success('🎉 Prize claim submitted! The team will message you on Messenger within 1-2 business days.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit claim. Try again or message us on Messenger.');
    }
    setClaiming(false);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-white/40 text-sm">Loading certificate…</div>;
  if (!data?.top) return (
    <main className="min-h-screen flex items-center justify-center text-center px-4">
      <div>
        <p className="text-white/60 text-sm font-sans mb-3">No top seller for {data?.month_label || `${y}/${m}`} yet.</p>
        <Link to="/ambassador" className="text-[#D4AF37] text-xs uppercase tracking-[0.2em] hover:underline">← Back to Dashboard</Link>
      </div>
    </main>
  );

  const t = data.top;
  const issuedDate = new Date(Date.UTC(y, m, 0)).toLocaleDateString('en-PH', { dateStyle: 'long' });

  return (
    <main className="min-h-screen bg-[#0A0A0A] py-8 sm:py-14 px-4 print:bg-white print:py-0 print:px-0">
      <SEOHead title={`Top Seller — ${data.month_label}`} robots="noindex,nofollow" />

      {/* Top toolbar (hidden on print) */}
      <div className="max-w-[10.5in] mx-auto flex flex-wrap items-center justify-between gap-3 mb-6 print:hidden">
        <Link to="/ambassador" className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/50 hover:text-white font-sans" data-testid="cert-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Dashboard
        </Link>
        <div className="flex items-center gap-2 flex-wrap">
          {/* "My Wins" picker — jump to any past month the ambassador has won.
              Personal to the viewer, so only render when an ambassador is signed in. */}
          {myAmbId && <MyWinsPicker currentYear={y} currentMonth={m} />}

          {/* Claim / status chip — ONLY rendered when the viewer is actually
              the winner. Non-winners just see the print/back buttons. */}
          {isMe && (claim ? (
            <span
              data-testid="cert-claim-status"
              className="inline-flex items-center gap-2 px-4 py-2 border border-[#1C7D3F] text-[#1C7D3F] text-xs uppercase tracking-[0.2em] font-sans"
            >
              <Gift className="w-3.5 h-3.5" />
              {claim.status === 'fulfilled' ? 'Prize Sent ✓' : claim.status === 'cancelled' ? 'Claim Cancelled' : 'Prize Requested'}
            </span>
          ) : !monthIsOver ? (
            <span
              data-testid="cert-claim-locked"
              title="Claims open the moment this month ends"
              className="inline-flex items-center gap-2 px-4 py-2 border border-white/15 text-white/50 text-xs uppercase tracking-[0.2em] font-sans"
            >
              <Lock className="w-3.5 h-3.5" /> Claim opens {new Date(y, m, 1).toLocaleDateString('en-PH', { month: 'short', day: 'numeric' })}
            </span>
          ) : claimExpired ? (
            // 7-day window elapsed — prize is forfeit. Cert still downloadable for personal flexing.
            <span
              data-testid="cert-claim-expired"
              title="Prize claim window has closed"
              className="inline-flex items-center gap-2 px-4 py-2 border border-red-400/40 text-red-400/80 text-xs uppercase tracking-[0.2em] font-sans"
            >
              <AlertCircle className="w-3.5 h-3.5" /> Prize Expired
            </span>
          ) : (
            <button
              type="button"
              onClick={handleClaim}
              disabled={claiming}
              className={`flex items-center gap-2 px-4 py-2 text-white text-xs uppercase tracking-[0.2em] transition-colors font-sans disabled:opacity-50 ${
                claimDaysLeft <= 2 ? 'bg-red-500 hover:bg-red-400 animate-pulse' : 'bg-[#1C7D3F] hover:bg-[#2E9B50]'
              }`}
              data-testid="cert-claim-prize-btn"
            >
              <Gift className="w-3.5 h-3.5" /> {claiming ? 'Submitting…' : `Claim Prize · ${claimDaysLeft}d left`}
            </button>
          ))}
          {monthIsOver ? (
            <button
              type="button"
              onClick={() => window.print()}
              className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.2em] hover:bg-[#E5C158] transition-colors font-sans"
              data-testid="cert-print-btn"
            >
              <Download className="w-3.5 h-3.5" /> Save as PDF
            </button>
          ) : (
            <span
              data-testid="cert-print-locked"
              title="Download unlocks at month-end"
              className="inline-flex items-center gap-2 px-4 py-2 border border-white/15 text-white/40 text-xs uppercase tracking-[0.2em] font-sans cursor-not-allowed"
            >
              <Lock className="w-3.5 h-3.5" /> Download Locked
            </span>
          )}
        </div>
      </div>

      {/* Motivational preview banner — shows only while the month is
          still in progress AND only to the actual top seller. Non-winners
          see a separate spectator banner so we don't leak "you're #1" to
          random ambassadors viewing the public certificate URL. */}
      {!monthIsOver && isMe && (
        <div
          className="max-w-[10.5in] mx-auto mb-5 bg-gradient-to-r from-[#D4AF37]/15 via-[#D4AF37]/8 to-transparent border-l-4 border-[#D4AF37] px-5 py-4 print:hidden"
          data-testid="cert-preview-banner"
        >
          <p className="text-[#D4AF37] text-xs uppercase tracking-[0.28em] font-sans flex items-center gap-2 mb-1">
            <Sparkles className="w-3.5 h-3.5" /> You&apos;re currently #1 — keep going
          </p>
          <p className="text-white/70 text-sm font-sans leading-relaxed">
            This is a sneak peek of <em>your</em> certificate. Stay on top of the leaderboard until <strong className="text-white">{new Date(y, m, 0).toLocaleDateString('en-PH', { month: 'long', day: 'numeric' })}</strong> to officially unlock the high-res download and claim your monthly prize bundle. The leaderboard can still flip — wag mag-relax. 🔥
          </p>
        </div>
      )}
      {!monthIsOver && !isMe && (
        <div
          className="max-w-[10.5in] mx-auto mb-5 bg-gradient-to-r from-white/5 via-white/2 to-transparent border-l-4 border-white/20 px-5 py-4 print:hidden"
          data-testid="cert-spectator-banner"
        >
          <p className="text-white/70 text-xs uppercase tracking-[0.28em] font-sans flex items-center gap-2 mb-1">
            <Sparkles className="w-3.5 h-3.5" /> Live preview — {t.name} is currently #1
          </p>
          <p className="text-white/50 text-sm font-sans leading-relaxed">
            Standings can still flip before <strong className="text-white/80">{new Date(y, m, 0).toLocaleDateString('en-PH', { month: 'long', day: 'numeric' })}</strong>. Push your sales and you could take the crown by month-end. 🔥
          </p>
        </div>
      )}

      {/* CERTIFICATE — fixed canvas, never splits across pages.
          Preview mode (!monthIsOver) gets an anti-screenshot wrapper class. */}
      <div className={`certificate-wrap ${!monthIsOver ? 'cert-preview-mode' : ''}`}>
        <article className="certificate-sheet" data-testid="certificate-sheet">
          {/* Giant diagonal PREVIEW watermark — only renders mid-month.
              Three tiled layers + screen-blend so a screenshot still
              shows the word clearly even if the user crops to the
              certificate body. */}
          {!monthIsOver && (
            <div className="cert-preview-overlay" aria-hidden="true" data-testid="cert-preview-watermark">
              <div className="cert-preview-text">PREVIEW</div>
              <div className="cert-preview-text">PREVIEW</div>
              <div className="cert-preview-text">PREVIEW</div>
            </div>
          )}

          {/* Outer ornate gold frame */}
          <div className="cert-frame-outer">
            <div className="cert-frame-mid">
              <div className="cert-frame-inner">

                {/* Watermark seal (subtle) */}
                <div className="cert-watermark" aria-hidden="true">
                  <Crown className="w-full h-full" strokeWidth={0.6} />
                </div>

                {/* Top corner ornaments */}
                <span className="cert-corner cert-corner-tl"><Sparkles className="w-5 h-5" /></span>
                <span className="cert-corner cert-corner-tr"><Sparkles className="w-5 h-5" /></span>
                <span className="cert-corner cert-corner-bl"><Sparkles className="w-5 h-5" /></span>
                <span className="cert-corner cert-corner-br"><Sparkles className="w-5 h-5" /></span>

                {/* Body */}
                <div className="cert-body">
                  <header className="cert-header">
                    <img src={LOGO_URL} alt="JS Fragrance" className="cert-logo" />
                    <p className="cert-eyebrow">Certificate of Distinction</p>
                    <h1 className="cert-title">Top Seller of the Month</h1>
                    <div className="cert-divider">
                      <span className="cert-divider-line"></span>
                      <Crown className="cert-divider-icon" strokeWidth={1.4} />
                      <span className="cert-divider-line"></span>
                    </div>
                    <p className="cert-month">{data.month_label}</p>
                  </header>

                  <section className="cert-citation">
                    <p className="cert-presented">This certificate is proudly presented to</p>
                    <h2 className="cert-recipient">{t.name}</h2>
                    <p className="cert-recognition">
                      In honour of exceptional craftsmanship in storytelling and selling — moving
                      <span className="cert-stat"> {t.bottles} </span>
                      bottles and generating
                      <span className="cert-stat"> ₱{Number(t.revenue).toLocaleString()} </span>
                      in revenue throughout {data.month_label}.
                      <br />A true ambassador of <em>Our Quality. Our Distinction.</em>
                    </p>
                  </section>

                  {/* Signature & seal block */}
                  <footer className="cert-footer">
                    <div className="cert-sig">
                      <p className="cert-sig-name">JS Fragrance™</p>
                      <span className="cert-sig-line"></span>
                      <p className="cert-sig-label">Brand Owner</p>
                    </div>

                    <div className="cert-seal" aria-hidden="true">
                      <div className="cert-seal-ring">
                        <Award className="cert-seal-icon" strokeWidth={1.4} />
                      </div>
                      <p className="cert-seal-label">Official Seal</p>
                    </div>

                    <div className="cert-sig cert-sig-right">
                      <p className="cert-sig-name">{issuedDate}</p>
                      <span className="cert-sig-line"></span>
                      <p className="cert-sig-label">Date Issued</p>
                    </div>
                  </footer>

                  {/* Serial number ribbon */}
                  <p className="cert-serial" data-testid="cert-serial">Certificate № {serial}</p>
                </div>

              </div>
            </div>
          </div>
        </article>
      </div>

      <style>{`
        /* === Certificate canvas (screen + print) === */
        .certificate-wrap {
          display: flex;
          justify-content: center;
          align-items: flex-start;
        }

        /* === Preview-mode protections ===
           When the month isn't over yet, the certificate is gated:
           the user can preview it (great motivator) but can't grab a
           clean download. We layer multiple deterrents:
             1. Giant tiled diagonal "PREVIEW" watermark across the sheet
             2. Disabled print + Save-as-PDF buttons (handled in JSX)
             3. CSS user-select: none / pointer-events: none / drag-disable
                on the certificate sheet to thwart casual copy/save
             4. @media print rule that hides the certificate entirely
                when in preview mode (so Cmd-P / Ctrl-P prints a blank page)
           Determined screenshots can't be fully blocked by web tech, but
           the giant diagonal watermark means a screenshot is still
           visibly stamped PREVIEW — useless for personal branding. */
        .cert-preview-mode .certificate-sheet {
          -webkit-user-select: none;
          user-select: none;
          -webkit-touch-callout: none;
          -webkit-user-drag: none;
        }
        .cert-preview-mode .certificate-sheet img {
          pointer-events: none;
          -webkit-user-drag: none;
          user-drag: none;
        }
        .cert-preview-overlay {
          position: absolute;
          inset: 0;
          z-index: 50;
          pointer-events: none;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: space-around;
          overflow: hidden;
          /* Slight tilt so the watermark crosses the sheet diagonally */
          transform: rotate(-22deg);
          transform-origin: center;
        }
        .cert-preview-text {
          font-family: 'Outfit', system-ui, sans-serif;
          font-weight: 800;
          font-size: 9.5rem;
          letter-spacing: 0.18em;
          color: rgba(212, 175, 55, 0.42);
          text-shadow:
            0 2px 0 rgba(0, 0, 0, 0.08),
            0 0 28px rgba(212, 175, 55, 0.18);
          white-space: nowrap;
          line-height: 0.9;
          /* Offset alternating rows so the watermark fully tiles the sheet
             even at the corners after the parent's rotation */
        }
        .cert-preview-text:nth-child(1) { transform: translateX(-6rem); }
        .cert-preview-text:nth-child(2) { transform: translateX(2rem); }
        .cert-preview-text:nth-child(3) { transform: translateX(-3rem); }

        .certificate-sheet {
          /* A landscape "A4-ish" fixed canvas. Critical: with these exact
             dimensions and the print CSS below, the certificate ALWAYS
             prints on a single page. */
          width: 10.5in;
          max-width: 100%;
          aspect-ratio: 10.5 / 7.5;
          background: linear-gradient(135deg, #fdfbf3 0%, #f6efd6 50%, #fdfbf3 100%);
          color: #1a1a1a;
          position: relative;
          box-shadow: 0 30px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(212,175,55,0.25);
        }
        /* Scale down on smaller screens so the whole thing fits without
           clipping. We never re-flow the inner layout — print stays pristine. */
        @media (max-width: 1100px) {
          .certificate-sheet { transform: scale(0.86); transform-origin: top center; }
        }
        @media (max-width: 900px) {
          .certificate-sheet { transform: scale(0.7); transform-origin: top center; }
        }
        @media (max-width: 700px) {
          .certificate-sheet { transform: scale(0.5); transform-origin: top center; }
        }
        @media (max-width: 480px) {
          .certificate-sheet { transform: scale(0.36); transform-origin: top center; }
        }

        /* === Triple ornate frame === */
        .cert-frame-outer {
          position: absolute; inset: 0.35in;
          border: 6px double #b8902b;
          background:
            linear-gradient(45deg, transparent 49.5%, rgba(212,175,55,0.08) 50%, transparent 50.5%) 0 0/24px 24px,
            linear-gradient(135deg, transparent 49.5%, rgba(212,175,55,0.08) 50%, transparent 50.5%) 0 0/24px 24px;
        }
        .cert-frame-mid {
          position: absolute; inset: 0.15in;
          border: 1px solid #b8902b;
        }
        .cert-frame-inner {
          position: absolute; inset: 0.12in;
          border: 2px solid #d4af37;
          background: linear-gradient(180deg, rgba(255,255,255,0.55) 0%, rgba(253,251,243,0.7) 100%);
          overflow: hidden;
        }

        /* === Body grid === */
        .cert-body {
          position: relative;
          height: 100%;
          padding: 0.55in 0.7in 0.45in;
          display: grid;
          grid-template-rows: auto 1fr auto;
          gap: 0.25in;
        }
        .cert-header { text-align: center; }
        .cert-logo {
          height: 0.85in; width: auto; margin: 0 auto 0.12in;
          filter: drop-shadow(0 1px 1px rgba(0,0,0,0.18));
        }
        .cert-eyebrow {
          font-family: 'Outfit', system-ui, sans-serif;
          font-size: 11px; letter-spacing: 0.36em;
          text-transform: uppercase; color: #8a6914;
          margin: 0 0 0.06in;
        }
        .cert-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 44px; line-height: 1; font-weight: 400;
          color: #1a1a1a; margin: 0 0 0.10in;
          letter-spacing: -0.01em;
        }
        .cert-divider {
          display: flex; align-items: center; justify-content: center;
          gap: 12px; margin: 0 auto 0.08in; max-width: 320px;
        }
        .cert-divider-line {
          flex: 1; height: 1px;
          background: linear-gradient(to right, transparent, #b8902b, transparent);
        }
        .cert-divider-icon { color: #b8902b; width: 22px; height: 22px; }
        .cert-month {
          font-family: 'Outfit', system-ui, sans-serif;
          font-size: 13px; letter-spacing: 0.22em; text-transform: uppercase;
          color: #6b5212; margin: 0;
        }

        /* === Citation === */
        .cert-citation {
          text-align: center;
          align-self: center;
          padding: 0 0.4in;
        }
        .cert-presented {
          font-family: 'Cormorant Garamond', serif;
          font-style: italic; font-size: 18px;
          color: #444; margin: 0 0 0.18in;
        }
        .cert-recipient {
          font-family: 'Cormorant Garamond', serif;
          font-size: 58px; font-weight: 500;
          color: #8a6914; line-height: 1.05;
          margin: 0 0 0.18in;
          letter-spacing: -0.01em;
          text-shadow: 0 1px 0 rgba(255,255,255,0.6);
        }
        .cert-recognition {
          font-family: 'Cormorant Garamond', serif;
          font-size: 16px; line-height: 1.55;
          color: #2b2b2b; margin: 0; max-width: 7.5in; margin-left: auto; margin-right: auto;
        }
        .cert-stat {
          font-weight: 600; color: #8a6914;
        }

        /* === Footer / signatures === */
        .cert-footer {
          display: grid;
          grid-template-columns: 1fr auto 1fr;
          align-items: end;
          gap: 0.4in;
          padding-top: 0.18in;
        }
        .cert-sig { text-align: center; }
        .cert-sig-right { text-align: center; }
        .cert-sig-name {
          font-family: 'Cormorant Garamond', serif;
          font-size: 22px; color: #1a1a1a; margin: 0 0 0.06in;
          font-style: italic;
        }
        .cert-sig-line {
          display: block; height: 1px; background: #1a1a1a; opacity: 0.55;
          width: 80%; margin: 0 auto 0.05in;
        }
        .cert-sig-label {
          font-family: 'Outfit', system-ui, sans-serif;
          font-size: 9px; letter-spacing: 0.28em;
          text-transform: uppercase; color: #6b5212; margin: 0;
        }
        .cert-seal {
          text-align: center;
        }
        .cert-seal-ring {
          width: 0.95in; height: 0.95in; border-radius: 50%;
          border: 3px double #b8902b;
          display: flex; align-items: center; justify-content: center;
          background: radial-gradient(circle at 35% 30%, #f3dc8a 0%, #d4af37 55%, #8a6914 100%);
          box-shadow: 0 2px 6px rgba(0,0,0,0.18), inset 0 0 12px rgba(255,255,255,0.4);
          margin: 0 auto 0.06in;
        }
        .cert-seal-icon { width: 0.5in; height: 0.5in; color: #fff; }
        .cert-seal-label {
          font-family: 'Outfit', system-ui, sans-serif;
          font-size: 8px; letter-spacing: 0.3em;
          text-transform: uppercase; color: #6b5212; margin: 0;
        }

        /* === Watermark === */
        .cert-watermark {
          position: absolute;
          inset: 0; display: flex; align-items: center; justify-content: center;
          color: #b8902b; opacity: 0.05; pointer-events: none;
        }
        .cert-watermark > svg { width: 5in; height: 5in; }

        /* === Corner ornaments === */
        .cert-corner {
          position: absolute; color: #b8902b; opacity: 0.65;
        }
        .cert-corner-tl { top: 0.18in; left: 0.18in; }
        .cert-corner-tr { top: 0.18in; right: 0.18in; }
        .cert-corner-bl { bottom: 0.18in; left: 0.18in; }
        .cert-corner-br { bottom: 0.18in; right: 0.18in; }

        /* === Serial === */
        .cert-serial {
          position: absolute; bottom: 0.08in; left: 0; right: 0;
          font-family: 'Outfit', system-ui, sans-serif;
          font-size: 8px; letter-spacing: 0.3em;
          text-transform: uppercase; color: #8a6914; opacity: 0.7;
          text-align: center; margin: 0;
        }

        /* ============== PRINT — pin to one A4 landscape page ============== */
        @media print {
          @page { size: A4 landscape; margin: 0; }
          html, body { background: white !important; margin: 0 !important; padding: 0 !important; }
          /* Hide everything except the certificate sheet (and its frame chain) */
          body * { visibility: hidden !important; }
          .certificate-sheet, .certificate-sheet * { visibility: visible !important; }
          /* Pin the sheet to fill the page exactly */
          .certificate-wrap { display: block; margin: 0; padding: 0; }
          .certificate-sheet {
            position: fixed;
            top: 0; left: 0;
            width: 297mm; height: 210mm;
            transform: none !important;
            box-shadow: none !important;
            page-break-inside: avoid !important;
            break-inside: avoid-page !important;
            overflow: hidden !important;
          }
          /* Trust paper white; gradients can banding-issue on cheap printers */
          .certificate-sheet { background: #fdfbf3 !important; }

          /* Preview mode: nuke the cert entirely from print output.
             Even if the user opens devtools and removes the lock UI,
             this rule means Ctrl-P / Save as PDF yields a blank page
             instead of a clean certificate. */
          .cert-preview-mode .certificate-sheet,
          .cert-preview-mode .certificate-sheet * {
            visibility: hidden !important;
          }
          .cert-preview-mode::after {
            content: "Certificate preview — download unlocks at month-end.";
            position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%);
            font-family: 'Outfit', system-ui, sans-serif; font-size: 14pt; color: #222;
            visibility: visible !important;
          }
        }
      `}</style>
    </main>
  );
}
